import { PageLayout } from '@/layouts/page-layout'
import { Form, Formik } from 'formik'
import { Button, Switch, Typography } from '@material-tailwind/react'
import { Link, useLoaderData, useNavigate, useRevalidator } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useGetCompanyQuery, useUpdateUserMutation } from '@/store/services'
import { list } from '@/router/utils'
import { useEffect, useState } from 'react'
import { confirm } from '@/shared/components/confirmation/confirmation.util'
import { useAuthorization } from '@/hooks'
import { HasAccess } from '@permify/react-role'
import { validationSchema as generateValidateSchema } from './schema-validation'
import { UserCompanyInformationForm, UserPersonalInformationForm } from './forms'

export const UserEdit = () => {
  const { hasAccess } = useAuthorization({
    redirection: {
      enable: true
    }
  })
  const { t } = useTranslation()
  const { user } = useLoaderData()
  const revalidator = useRevalidator()
  const navigate = useNavigate()
  const [updateUser, { isLoading }] = useUpdateUserMutation()
  const { data: { company = {} } = {} } = useGetCompanyQuery(user?.resource?.id, { skip: !user?.resource?.id })
  const [selectedRole, setSelectedRole] = useState(user?.roles[0] || {})

  const initialValues = {
    id: user.id,
    first_name: user.firstName,
    last_name: user.lastName,
    email: user.email,
    document_number: user.documentNumber || null,
    phone: user.phone,
    role_ids: [user?.roles[0]?.id],
    resource_type: company?.id ? 'Contractor::Company' : null,
    resource_id: company?.id ? company.id : null,
    has_local_document_number: user.hasLocalDocumentNumber,
    account_office_id: user.office?.id,
    account_managment_id: user.managment?.id
  }

  const onSubmit = async (values) => {
    try {
      await updateUser(values).unwrap()
      navigate(list('users'))
    } catch (error) {
      console.log(error)
    }
  }

  const handleStatusChange = async () => {
    const status = user.active ? 'active' : 'inactive'
    if (await confirm({
      title: t(`modal.confirm.status.${status}.title`),
      description: t(`modal.confirm.status.${status}.message`)
    })) {
      await updateUser({ id: user.id, active: !user.active }).unwrap()
      revalidator.revalidate()
    }
  }

  useEffect(() => {
    hasAccess('user#edit')
  }, [])
  return (
    <PageLayout
      title={
        t('actions.edit.with', { what: t('user.one') })
      }
      actions={
        <HasAccess permissions='user#update'>
          <div className='px-4 py-4 bg-gray-50 rounded-2xl flex gap-3'>
            <Switch
              checked={user.active}
              onChange={handleStatusChange}
            />
            <Typography variant='small' className='text-gray-500 font-semibold'>
              {
                user.active
                  ? 'Usuario activo'
                  : 'Usuario inactivo'
              }
            </Typography>
          </div>
        </HasAccess>
      }
    >
      <Formik enableReinitialize validateOnChange initialValues={initialValues} onSubmit={onSubmit} validationSchema={generateValidateSchema(selectedRole?.name || '')} >
        <Form >
          <UserPersonalInformationForm setSelectedRole={setSelectedRole} />
          <UserCompanyInformationForm company={company} />
          <div className='flex justify-end gap-3 mt-10'>
            <Link to={list('users')}>
              <Button variant='text' type='submit'>
                {t('actions.cancel')}
              </Button>
            </Link>

            <HasAccess permissions='user#update'>
              <Button type='submit' loading={isLoading}>
                {t('actions.update.one')}
              </Button>
            </HasAccess>
          </div>
        </Form>
      </Formik>
    </PageLayout >
  )
}
